import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Breadcrumbs,
  Link,
  Typography,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Folder, Description, ChevronRight, Search, Clear } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';
import sharepointSyncService from '../services/sharepoint-sync.service';

interface SharePointBrowserProps {
  open: boolean;
  onClose: () => void;
  onSelect: (driveId: string, driveName: string, folderPath?: string, folderId?: string, fileName?: string, fileId?: string, selectedFiles?: any[]) => void;
  title?: string;
  selectionMode?: 'folder' | 'file' | 'multi-file'; // NEW: Support multi-file selection
  fileFilter?: string[]; // NEW: Filter by file extensions (e.g., ['.docx', '.pdf'])
  excludePatterns?: string[]; // NEW: Exclude files matching these patterns (e.g., ['Resumo', 'Summary'])
  rememberLocation?: boolean; // PPTXC-RQ-09: Remember last location when adding more files
  initialPath?: { // PPTXC-RQ-12: Open directly in specified folder (used on first open)
    siteName: string;
    driveName: string;
    folderPath?: string;
  };
}

const SharePointBrowser: React.FC<SharePointBrowserProps> = ({
  open,
  onClose,
  onSelect,
  title = 'Select SharePoint Library',
  selectionMode = 'folder', // Default to folder mode for backward compatibility
  fileFilter = [], // Default to no filter
  excludePatterns = [], // Default to no exclusions
  rememberLocation = false, // PPTXC-RQ-09: Default to false for backward compatibility
  initialPath, // PPTXC-RQ-12: Optional initial navigation path
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sites, setSites] = useState<any[]>([]);
  const [selectedSite, setSelectedSite] = useState<any | null>(null);
  const [libraries, setLibraries] = useState<any[]>([]);
  const [selectedLibrary, setSelectedLibrary] = useState<any | null>(null);

  // Folder navigation state
  const [folders, setFolders] = useState<any[]>([]);
  const [folderBreadcrumbs, setFolderBreadcrumbs] = useState<any[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [viewingFolders, setViewingFolders] = useState(false);

  // File selection state (NEW)
  const [selectedFile, setSelectedFile] = useState<any | null>(null);

  // Folder radio selection state (NEW)
  const [selectedFolderForRadio, setSelectedFolderForRadio] = useState<any | null>(null);

  // Multi-file selection state (NEW)
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

  // PPTXC-RQ-10: Search/filter state
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (open) {
      // PPTXC-RQ-12: If initialPath is provided, ALWAYS navigate to it (takes precedence)
      if (initialPath) {
        navigateToInitialPath();
      } else if (rememberLocation && selectedLibrary) {
        // PPTXC-RQ-09: If rememberLocation is enabled and we have saved state, restore it
        // The saved state is already in component state (selectedSite, selectedLibrary, etc.)
        if (viewingFolders) {
          loadFolders(selectedLibrary.id, currentFolderId);
        }
        // Don't call loadSites() - we already have the navigation state
      } else {
        // Fresh start - load from beginning
        loadSites();
      }
    }
  }, [open]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  };

  const loadSites = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/sharepoint-sync/sites`, {
        headers: getAuthHeaders(),
      });

      // Filter out personal workspace/contentstorage sites (they don't allow app access)
      const allSites = response.data.data || [];
      const accessibleSites = allSites.filter((site: any) => {
        // Exclude sites with 'contentstorage' in URL (personal workspaces, OneDrive, Designer sites)
        const isPersonalWorkspace = site.webUrl?.includes('/contentstorage/');
        const isMyWorkspace = site.displayName?.toLowerCase().includes('meu espaço de trabalho') ||
                              site.displayName?.toLowerCase().includes('my workspace');
        const isDesigner = site.displayName?.toLowerCase().includes('designer') && isPersonalWorkspace;

        return !isPersonalWorkspace && !isMyWorkspace && !isDesigner;
      });

      console.log(`[SharePointBrowser] Filtered ${allSites.length - accessibleSites.length} personal workspace sites`);
      setSites(accessibleSites);
    } catch (error: any) {
      setError(error.message || 'Failed to load SharePoint sites');
    } finally {
      setLoading(false);
    }
  };

  const loadLibraries = async (siteId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_BASE_URL}/sharepoint-sync/sites/${siteId}/libraries`,
        {
          headers: getAuthHeaders(),
        }
      );

      setLibraries(response.data.data || []);
    } catch (error: any) {
      // Check if it's an access denied error
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load libraries';

      if (errorMessage.toLowerCase().includes('access denied') || error.response?.status === 403) {
        setError('⚠️ Access Denied: This site has restricted access. Please select a different SharePoint site or contact your administrator.');
      } else {
        setError(errorMessage);
      }

      console.error('[SharePointBrowser] Error loading libraries:', error);
      // Go back to site selection on error
      setSelectedSite(null);
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async (driveId: string, folderId: string | null = null) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedFolderForRadio(null); // Reset folder selection when navigating

      // Use sharepointSyncService to get drive items
      const items = await sharepointSyncService.getDriveItems(driveId, folderId || undefined);
      setFolders(items);

      // Get breadcrumbs if in a subfolder
      if (folderId) {
        const breadcrumbs = await sharepointSyncService.getBreadcrumbs(driveId, folderId);
        setFolderBreadcrumbs(breadcrumbs);
      } else {
        setFolderBreadcrumbs([]);
      }

      setCurrentFolderId(folderId);
    } catch (error: any) {
      setError(error.message || 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  // PPTXC-RQ-12: Navigate to initial path on first open
  const navigateToInitialPath = async () => {
    if (!initialPath) return;

    try {
      setLoading(true);
      setError(null);

      // Step 1: Load sites
      const response = await axios.get(`${API_BASE_URL}/sharepoint-sync/sites`, {
        headers: getAuthHeaders(),
      });
      const allSites = response.data.data || [];
      const accessibleSites = allSites.filter((site: any) => {
        const isPersonalWorkspace = site.webUrl?.includes('/contentstorage/');
        const isMyWorkspace = site.displayName?.toLowerCase().includes('meu espaço de trabalho') ||
                              site.displayName?.toLowerCase().includes('my workspace');
        const isDesigner = site.displayName?.toLowerCase().includes('designer') && isPersonalWorkspace;
        return !isPersonalWorkspace && !isMyWorkspace && !isDesigner;
      });
      setSites(accessibleSites);

      // Step 2: Find site by name
      const site = accessibleSites.find((s: any) =>
        s.displayName === initialPath.siteName || s.name === initialPath.siteName
      );

      if (!site) {
        setError(`Site "${initialPath.siteName}" not found`);
        setLoading(false);
        return;
      }

      setSelectedSite(site);

      // Step 3: Load libraries for the site
      const libResponse = await axios.get(
        `${API_BASE_URL}/sharepoint-sync/sites/${site.id}/libraries`,
        { headers: getAuthHeaders() }
      );
      const libs = libResponse.data.data || [];
      setLibraries(libs);

      // Step 4: Find library by name
      const library = libs.find((l: any) => l.name === initialPath.driveName);

      if (!library) {
        setError(`Library "${initialPath.driveName}" not found`);
        setLoading(false);
        return;
      }

      setSelectedLibrary(library);
      setViewingFolders(true);

      // Step 5: Navigate to folder if path provided
      if (initialPath.folderPath) {
        try {
          // Navigate step by step through folder hierarchy
          const pathParts = initialPath.folderPath.split('/').filter(Boolean);
          let currentFolderId: string | null = null;
          const breadcrumbs: any[] = [];

          // Navigate through each folder in the path
          for (const folderName of pathParts) {
            const items = await sharepointSyncService.getDriveItems(library.id, currentFolderId || undefined);

            // Find the folder by name
            const folder = items.find(item =>
              item.folder && item.name === folderName
            );

            if (!folder) {
              console.warn(`[SharePointBrowser] Folder "${folderName}" not found in path`);
              throw new Error(`Folder "${folderName}" not found`);
            }

            currentFolderId = folder.id;
            breadcrumbs.push({ name: folderName, id: folder.id });
          }

          // Load items from final folder
          const finalItems = await sharepointSyncService.getDriveItems(library.id, currentFolderId || undefined);
          setFolders(finalItems);
          setCurrentFolderId(currentFolderId);
          setFolderBreadcrumbs(breadcrumbs);

          console.log(`[SharePointBrowser] Navigated to: ${initialPath.folderPath}`);
        } catch (pathError: any) {
          console.warn('[SharePointBrowser] Could not navigate to path, loading root:', pathError);
          // Fall back to root if path not found
          const items = await sharepointSyncService.getDriveItems(library.id);
          setFolders(items);
          setCurrentFolderId(null);
          setFolderBreadcrumbs([]);
        }
      } else {
        // Load root folder
        const items = await sharepointSyncService.getDriveItems(library.id);
        setFolders(items);
        setCurrentFolderId(null);
        setFolderBreadcrumbs([]);
      }

      console.log('[SharePointBrowser] Navigated to initial path:', initialPath);

    } catch (error: any) {
      console.warn('[SharePointBrowser] Could not navigate to initial path, starting at root:', error);
      // Don't show error to user - just fall back to root (this is expected behavior)
      // Fall back to loading sites
      loadSites();
    } finally {
      setLoading(false);
    }
  };

  const handleSiteClick = (site: any) => {
    setSelectedSite(site);
    setSelectedLibrary(null);
    loadLibraries(site.id);
  };

  const handleLibraryClick = (library: any) => {
    setSelectedLibrary(library);
    setViewingFolders(true);
    loadFolders(library.id, null);
  };

  const handleFolderClick = (folder: any) => {
    if (selectedLibrary) {
      loadFolders(selectedLibrary.id, folder.id);
    }
  };

  const handleFileClick = (file: any) => {
    // In file selection mode, select the file
    if (selectionMode === 'file') {
      setSelectedFile(file);
    }
  };

  const handleFileToggle = (file: any) => {
    // Multi-file selection mode - toggle checkbox
    if (selectionMode === 'multi-file') {
      const isSelected = selectedFiles.some(f => f.id === file.id);
      if (isSelected) {
        // Remove from selection
        setSelectedFiles(selectedFiles.filter(f => f.id !== file.id));
      } else {
        // Add to selection with folder path info
        const folderPath = folderBreadcrumbs.map(b => b.name).join('/');
        setSelectedFiles([...selectedFiles, {
          ...file,
          folderPath,
          libraryName: selectedLibrary?.name
        }]);
      }
    }
  };

  const isFileAllowed = (fileName: string): boolean => {
    // Check file extension filter
    if (fileFilter.length > 0) {
      const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
      if (!fileFilter.some(ext => ext.toLowerCase() === extension)) {
        return false;
      }
    }

    // Check exclude patterns
    if (excludePatterns.length > 0) {
      const lowerFileName = fileName.toLowerCase();
      if (excludePatterns.some(pattern => lowerFileName.includes(pattern.toLowerCase()))) {
        return false; // Exclude files matching any pattern
      }
    }

    return true;
  };

  const handleFolderBreadcrumbClick = (index: number) => {
    if (!selectedLibrary) return;

    if (index === -1) {
      // Go back to library root
      loadFolders(selectedLibrary.id, null);
    } else {
      // Navigate to specific folder in breadcrumb
      const folder = folderBreadcrumbs[index];
      loadFolders(selectedLibrary.id, folder.id);
    }
  };

  const handleSelectCurrentFolder = () => {
    if (selectedLibrary && selectedFolderForRadio) {
      // Use the selected folder from radio button
      const folderPath = folderBreadcrumbs.length > 0
        ? `${folderBreadcrumbs.map(b => b.name).join('/')}/${selectedFolderForRadio.name}`
        : selectedFolderForRadio.name;
      onSelect(selectedLibrary.id, selectedLibrary.name, folderPath, selectedFolderForRadio.id);
      handleClose();
    } else if (selectedLibrary && !selectedFolderForRadio) {
      // If no folder selected but library is selected (shouldn't happen with radio buttons)
      onSelect(selectedLibrary.id, selectedLibrary.name);
      handleClose();
    }
  };

  const handleSelect = () => {
    // Multi-file selection mode
    if (selectionMode === 'multi-file' && selectedFiles.length > 0 && selectedLibrary) {
      onSelect(
        selectedLibrary.id,
        selectedLibrary.name,
        undefined, // folderPath
        undefined, // folderId
        undefined, // fileName
        undefined, // fileId
        selectedFiles // Pass all selected files
      );
      handleClose();
      return;
    }

    // File selection mode
    if (selectionMode === 'file' && selectedFile && selectedLibrary) {
      const folderPath = folderBreadcrumbs.map(b => b.name).join('/');
      onSelect(
        selectedLibrary.id,
        selectedLibrary.name,
        folderPath || undefined,
        currentFolderId || undefined,
        selectedFile.name, // fileName
        selectedFile.id    // fileId
      );
      handleClose();
      return;
    }

    // Folder selection mode (original behavior)
    if (selectedLibrary && !viewingFolders) {
      onSelect(selectedLibrary.id, selectedLibrary.name);
      handleClose();
    } else if (viewingFolders) {
      handleSelectCurrentFolder();
    }
  };

  const handleClose = () => {
    // PPTXC-RQ-09: Conditionally preserve navigation state
    if (rememberLocation) {
      // Keep navigation state: site, library, folders, breadcrumbs, currentFolderId, viewingFolders
      // Only reset selection state so user can select another file
      setSelectedFile(null);
      setSelectedFolderForRadio(null);
      setSelectedFiles([]);
      setSearchQuery(''); // PPTXC-RQ-10: Reset search query for fresh view
    } else {
      // Original behavior: reset everything
      setSelectedSite(null);
      setSelectedLibrary(null);
      setLibraries([]);
      setFolders([]);
      setFolderBreadcrumbs([]);
      setCurrentFolderId(null);
      setViewingFolders(false);
      setSelectedFile(null);
      setSelectedFolderForRadio(null);
      setSelectedFiles([]);
      setSearchQuery(''); // PPTXC-RQ-10: Reset search query
    }
    onClose();
  };

  const handleBack = () => {
    if (viewingFolders) {
      // Go back from folders to libraries
      setViewingFolders(false);
      setFolders([]);
      setFolderBreadcrumbs([]);
      setCurrentFolderId(null);
      setSelectedFolderForRadio(null);
    } else if (selectedSite && !selectedLibrary) {
      // Go back from libraries to sites
      setSelectedSite(null);
      setLibraries([]);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => {
              setSelectedSite(null);
              setSelectedLibrary(null);
              setLibraries([]);
              setFolders([]);
              setViewingFolders(false);
            }}
            sx={{ cursor: 'pointer' }}
          >
            Sites
          </Link>
          {selectedSite && !viewingFolders && (
            <Typography color="text.primary">{selectedSite.displayName}</Typography>
          )}
          {selectedSite && viewingFolders && (
            <Link
              component="button"
              variant="body1"
              onClick={() => {
                setViewingFolders(false);
                setFolders([]);
                setFolderBreadcrumbs([]);
              }}
              sx={{ cursor: 'pointer' }}
            >
              {selectedSite.displayName}
            </Link>
          )}
          {selectedLibrary && viewingFolders && (
            <>
              <Link
                component="button"
                variant="body1"
                onClick={() => handleFolderBreadcrumbClick(-1)}
                sx={{ cursor: 'pointer' }}
              >
                {selectedLibrary.name}
              </Link>
              {folderBreadcrumbs.map((breadcrumb, index) => (
                <Link
                  key={breadcrumb.id || `breadcrumb-${index}`}
                  component="button"
                  variant="body1"
                  onClick={() => handleFolderBreadcrumbClick(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  {breadcrumb.name}
                </Link>
              ))}
            </>
          )}
        </Breadcrumbs>

        {/* Helpful info message when showing sites */}
        {!selectedSite && sites.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Tip:</strong> Select your main SharePoint portal site (e.g., "FK Partners Portal") for best access.
            Personal workspace sites are filtered out.
          </Alert>
        )}

        {/* Warning when showing libraries - guide users to select correct library */}
        {selectedSite && !viewingFolders && libraries.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>⚠️ Important:</strong> For Apostila assembly, please select <strong>"Apostila - Conteúdo"</strong> library (not "Conteúdo").
            <br />
            For Resumo, select <strong>"Resumo - Conteúdo"</strong> library.
            <br />
            <em>Avoid using old content from "Histórico de Edições" folders.</em>
          </Alert>
        )}

        {/* Info when viewing folders - remind about correct folder structure */}
        {viewingFolders && selectionMode === 'folder' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>📁 Folder Selection:</strong> Click arrow (→) to navigate inside folders, or select radio button to choose folder.
            <br />
            <strong>Correct path:</strong> Objetos de Aprendizagem → 000 - Objetos Apostilas → [Your Course] → Apostila → Select OE folder
            <br />
            <em>⚠️ Avoid "Apostila Histórico de Edições" - it contains outdated content with old layout.</em>
          </Alert>
        )}

        {/* Show selected files in multi-file mode */}
        {selectionMode === 'multi-file' && selectedFiles.length > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              ✓ Selected {selectedFiles.length} file(s):
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {selectedFiles.map((file) => (
                <Chip
                  key={file.id}
                  label={file.name}
                  size="small"
                  onDelete={() => handleFileToggle(file)}
                  sx={{ maxWidth: '200px' }}
                />
              ))}
            </Stack>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* PPTXC-RQ-10: Search/filter input - only show when viewing folders/files */}
        {viewingFolders && folders.length > 0 && (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder="Search files by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            {searchQuery && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {(() => {
                  const filtered = folders.filter(item =>
                    item.name.toLowerCase().includes(searchQuery.toLowerCase())
                  );
                  return `Showing ${filtered.length} of ${folders.length} items`;
                })()}
              </Typography>
            )}
          </>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
            {!selectedSite ? (
              // Show sites
              <List>
                {sites.map((site) => (
                  <ListItem key={site.id} disablePadding>
                    <ListItemButton onClick={() => handleSiteClick(site)}>
                      <ListItemIcon>
                        <Folder />
                      </ListItemIcon>
                      <ListItemText
                        primary={site.displayName || site.name}
                        secondary={site.webUrl}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : viewingFolders ? (
              // Show folders/files - mode depends on selectionMode
              selectionMode === 'folder' ? (
                // Folder selection mode with radio buttons AND navigation
                <RadioGroup
                  value={selectedFolderForRadio?.id || ''}
                  onChange={(e) => {
                    const folder = folders.find(f => f.id === e.target.value);
                    setSelectedFolderForRadio(folder);
                  }}
                >
                  <List>
                    {folders.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary="No items found"
                          secondary="This folder is empty"
                        />
                      </ListItem>
                    ) : (
                      folders
                        .filter((item) => {
                          // Only show folders
                          if (!item.folder) return false;
                          // PPTXC-RQ-10: Filter by search query (case-insensitive)
                          if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                            return false;
                          }
                          return true;
                        })
                        .map((item) => (
                          <ListItem key={item.id} disablePadding>
                            <FormControlLabel
                              value={item.id}
                              control={<Radio />}
                              label={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    width: '100%',
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Folder />
                                    <Box>
                                      <Typography variant="body1">{item.name}</Typography>
                                      <Typography variant="body2" color="text.secondary">Folder</Typography>
                                    </Box>
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFolderClick(item);
                                    }}
                                    sx={{ ml: 'auto' }}
                                  >
                                    <ChevronRight />
                                  </IconButton>
                                </Box>
                              }
                              sx={{ width: '100%', m: 0, py: 1, px: 2 }}
                            />
                          </ListItem>
                        ))
                    )}
                  </List>
                </RadioGroup>
              ) : selectionMode === 'multi-file' ? (
                // Multi-file selection mode with checkboxes
                <List>
                  {folders.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="No items found"
                        secondary="This folder is empty"
                      />
                    </ListItem>
                  ) : (
                    folders
                      .filter((item) => {
                        // Filter by file extension if specified
                        if (!item.folder && fileFilter.length > 0) {
                          if (!isFileAllowed(item.name)) return false;
                        }
                        // PPTXC-RQ-10: Filter by search query (case-insensitive)
                        if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                          return false;
                        }
                        return true;
                      })
                      .map((item) => {
                        const isFolder = !!item.folder;
                        const isFileSelected = selectedFiles.some(f => f.id === item.id);

                        return (
                          <ListItem key={item.id} disablePadding>
                            <ListItemButton
                              onClick={() => isFolder ? handleFolderClick(item) : handleFileToggle(item)}
                            >
                              <ListItemIcon>
                                {isFolder ? (
                                  <Folder />
                                ) : (
                                  <Checkbox
                                    checked={isFileSelected}
                                    onChange={() => handleFileToggle(item)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                              </ListItemIcon>
                              <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                                {isFolder ? <Folder /> : <Description />}
                              </ListItemIcon>
                              <ListItemText
                                primary={item.name}
                                secondary={isFolder ? 'Folder' : `File • ${item.size ? Math.round(item.size / 1024) + ' KB' : 'Unknown size'}`}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })
                  )}
                </List>
              ) : (
                // Single file selection mode (original behavior)
                <List>
                  {folders.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="No items found"
                        secondary="This folder is empty"
                      />
                    </ListItem>
                  ) : (
                    folders
                      .filter((item) => {
                        if (selectionMode === 'file' && !item.folder && fileFilter.length > 0) {
                          if (!isFileAllowed(item.name)) return false;
                        }
                        // PPTXC-RQ-10: Filter by search query (case-insensitive)
                        if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                          return false;
                        }
                        return true;
                      })
                      .map((item) => {
                        const isFolder = !!item.folder;
                        const isDisabled = !isFolder && selectionMode !== 'file';
                        const isSelected = selectionMode === 'file' && selectedFile?.id === item.id;

                        return (
                          <ListItem key={item.id} disablePadding>
                            <ListItemButton
                              onClick={() => isFolder ? handleFolderClick(item) : handleFileClick(item)}
                              disabled={isDisabled}
                              selected={isSelected}
                            >
                              <ListItemIcon>
                                {isFolder ? <Folder /> : <Description />}
                              </ListItemIcon>
                              <ListItemText
                                primary={item.name}
                                secondary={isFolder ? 'Folder' : `File • ${item.size ? Math.round(item.size / 1024) + ' KB' : 'Unknown size'}`}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })
                  )}
                </List>
              )
            ) : (
              // Show libraries
              <List>
                {libraries.map((library) => (
                  <ListItem key={library.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleLibraryClick(library)}
                      selected={selectedLibrary?.id === library.id}
                    >
                      <ListItemIcon>
                        <Description />
                      </ListItemIcon>
                      <ListItemText
                        primary={library.name}
                        secondary={`${library.driveType || 'documentLibrary'}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {(selectedSite || viewingFolders) && (
          <Button onClick={handleBack} sx={{ mr: 'auto' }}>
            Back
          </Button>
        )}
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSelect}
          disabled={
            selectionMode === 'multi-file'
              ? selectedFiles.length === 0  // In multi-file mode, require at least one file
              : selectionMode === 'file'
                ? !selectedFile  // In file mode, require file selection
                : viewingFolders
                  ? !selectedFolderForRadio  // In folder view, require folder selection via radio
                  : !selectedLibrary  // In library view, require library selection
          }
        >
          {selectionMode === 'multi-file'
            ? `Select ${selectedFiles.length} File(s)`
            : selectionMode === 'file'
              ? (selectedFile ? `Select "${selectedFile.name}"` : 'Select File')
              : (viewingFolders
                  ? (selectedFolderForRadio ? `Select "${selectedFolderForRadio.name}"` : 'Select Folder')
                  : 'Select')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharePointBrowser;
